import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { User, UserRole, UserStatus, CreateUserForm, SubscriptionPlan } from '../../types';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [, setLoading] = useState(true);
  const [filter, setFilter] = useState({ role: '', status: '', search: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadUsers();
  }, [filter, page]);

  const loadUsers = async () => {
    setLoading(true);
    const response = await api.getUsers({
      page,
      pageSize: 10,
      role: filter.role || undefined,
      status: filter.status || undefined,
      search: filter.search || undefined,
    });
    if (response.success && response.data) {
      setUsers(response.data.data);
      setTotalPages(response.data.totalPages);
    }
    setLoading(false);
  };

  const handleApprove = async (userId: string) => {
    const response = await api.approveUser(userId);
    if (response.success) {
      loadUsers();
    }
  };

  const handleSuspend = async (userId: string) => {
    if (window.confirm('Are you sure you want to suspend this user?')) {
      const response = await api.suspendUser(userId);
      if (response.success) {
        loadUsers();
      }
    }
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      const response = await api.deleteUser(userId);
      if (response.success) {
        loadUsers();
      }
    }
  };

  const getStatusBadge = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'suspended':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'inactive':
        return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
      default:
        return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
    }
  };

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'reseller':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'client':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default:
        return 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
    }
  };

  // Mock users for demo
  const mockUsers: User[] = users.length > 0 ? users : [
    { id: '1', email: 'admin@geopro.com', role: 'admin', status: 'active', firstName: 'System', lastName: 'Admin', createdAt: '2024-01-01', updatedAt: '2024-01-01', loginCount: 150 },
    { id: '2', email: 'reseller1@example.com', role: 'reseller', status: 'active', firstName: 'Ahmed', lastName: 'Rahman', companyName: 'Digital Solutions BD', createdAt: '2024-02-15', updatedAt: '2024-02-15', loginCount: 45 },
    { id: '3', email: 'client1@example.com', role: 'client', status: 'active', firstName: 'Karim', lastName: 'Hassan', companyName: 'Dhaka Grocery', createdAt: '2024-03-01', updatedAt: '2024-03-01', loginCount: 28 },
    { id: '4', email: 'newclient@example.com', role: 'client', status: 'pending', firstName: 'New', lastName: 'Client', createdAt: '2024-03-10', updatedAt: '2024-03-10', loginCount: 0 },
    { id: '5', email: 'suspended@example.com', role: 'client', status: 'suspended', firstName: 'Suspended', lastName: 'User', createdAt: '2024-01-20', updatedAt: '2024-03-05', loginCount: 12 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-neutral-500">Manage all platform users</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors text-sm font-medium flex items-center gap-2"
        >
          <span>+</span> Create User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#121212] border border-[#262626] rounded-2xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by email or name..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white placeholder-neutral-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filter.role}
            onChange={(e) => setFilter({ ...filter, role: e.target.value })}
            className="bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="reseller">Reseller</option>
            <option value="client">Client</option>
          </select>

          {/* Status Filter */}
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626] bg-[#1a1a1a]">
                <th className="text-left px-6 py-4 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">User</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Role</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Status</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Logins</th>
                <th className="text-left px-6 py-4 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Created</th>
                <th className="text-right px-6 py-4 text-[10px] font-bold text-neutral-500 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#262626]">
              {mockUsers.map((user) => (
                <tr key={user.id} className="hover:bg-[#1a1a1a] transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#262626] rounded-full flex items-center justify-center text-lg">
                        {user.firstName?.[0] || user.email[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email.split('@')[0]}
                        </p>
                        <p className="text-neutral-500 text-sm">{user.email}</p>
                        {user.companyName && (
                          <p className="text-neutral-600 text-xs">{user.companyName}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase rounded border ${getRoleBadge(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase rounded border ${getStatusBadge(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-neutral-400">
                    {user.loginCount}
                  </td>
                  <td className="px-6 py-4 text-neutral-400 text-sm">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {user.status === 'pending' && (
                        <button
                          onClick={() => handleApprove(user.id)}
                          className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-medium hover:bg-emerald-500/30 transition-colors"
                        >
                          Approve
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="p-2 text-neutral-400 hover:text-white hover:bg-[#262626] rounded-lg transition-colors"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      {user.status === 'active' && user.role !== 'admin' && (
                        <button
                          onClick={() => handleSuspend(user.id)}
                          className="p-2 text-neutral-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors"
                          title="Suspend"
                        >
                          ⚠️
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-2 text-neutral-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-[#262626] flex items-center justify-between">
          <p className="text-neutral-500 text-sm">
            Showing {mockUsers.length} users
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 bg-[#1a1a1a] border border-[#262626] rounded-lg text-neutral-400 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#262626] transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-neutral-400">
              Page {page} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 bg-[#1a1a1a] border border-[#262626] rounded-lg text-neutral-400 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#262626] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadUsers();
          }}
        />
      )}

      {/* Edit User Modal */}
      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdated={() => {
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

// Create User Modal Component
const CreateUserModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState<CreateUserForm>({
    email: '',
    password: '',
    role: 'client',
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
    planId: '',
    paymentReference: '',
  });
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load plans when role is client
  useEffect(() => {
    if (form.role === 'client') {
      loadPlans();
    }
  }, [form.role]);

  const loadPlans = async () => {
    setLoadingPlans(true);
    const response = await api.getPublicPlans();
    if (response.success && response.data) {
      const activePlans = response.data.filter(p => p.isActive);
      setPlans(activePlans);
    }
    setLoadingPlans(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const response = await api.createUser(form);
    if (response.success) {
      onCreated();
    } else {
      setError(response.error || 'Failed to create user');
    }
    setLoading(false);
  };

  const selectedPlan = plans.find(p => p.id === form.planId);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#262626] flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Create New User</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Password *</label>
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Role *</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole, planId: '' })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="client">Client</option>
              <option value="reseller">Reseller</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Company Name</label>
            <input
              type="text"
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          {/* Subscription Plan Selection - Only for Clients */}
          {form.role === 'client' && (
            <>
              <div className="pt-2 border-t border-[#262626]">
                <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-3">
                  Subscription Plan (Optional)
                </label>
                {loadingPlans ? (
                  <div className="flex justify-center py-4">
                    <div className="w-6 h-6 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, planId: '' })}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        !form.planId
                          ? 'bg-neutral-500/10 border-neutral-500/50'
                          : 'bg-[#1a1a1a] border-[#262626] hover:border-[#363636]'
                      }`}
                    >
                      <p className="font-medium text-neutral-400">No Subscription</p>
                      <p className="text-xs text-neutral-500">Create without a plan</p>
                    </button>
                    {plans.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setForm({ ...form, planId: plan.id })}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          form.planId === plan.id
                            ? 'bg-emerald-500/10 border-emerald-500/50'
                            : 'bg-[#1a1a1a] border-[#262626] hover:border-[#363636]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{plan.name}</p>
                            <p className="text-xs text-neutral-500">{plan.features.length} features • {plan.durationDays} days</p>
                          </div>
                          <p className="text-emerald-400 font-bold">৳{plan.priceBdt.toLocaleString()}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Reference - Only shown when plan is selected */}
              {form.planId && (
                <div>
                  <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Payment Reference</label>
                  <input
                    type="text"
                    value={form.paymentReference}
                    onChange={(e) => setForm({ ...form, paymentReference: e.target.value })}
                    placeholder="bKash TXN ID, bank ref, etc."
                    className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              )}

              {/* Plan Summary */}
              {selectedPlan && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <p className="text-emerald-400 text-sm font-medium mb-2">Selected Plan: {selectedPlan.name}</p>
                  <div className="flex justify-between text-xs text-neutral-400">
                    <span>Price: ৳{selectedPlan.priceBdt.toLocaleString()}</span>
                    <span>Duration: {selectedPlan.durationDays} days</span>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-[#1a1a1a] border border-[#262626] text-neutral-400 rounded-xl hover:bg-[#262626] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-neutral-700 text-white rounded-xl font-medium transition-colors"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal (simplified)
const EditUserModal: React.FC<{ user: User; onClose: () => void; onUpdated: () => void }> = ({ user, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    companyName: user.companyName || '',
    status: user.status,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const response = await api.updateUser(user.id, form);
    if (response.success) {
      onUpdated();
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-[#262626] flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Edit User</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 uppercase tracking-widest mb-2">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as UserStatus })}
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-[#1a1a1a] border border-[#262626] text-neutral-400 rounded-xl hover:bg-[#262626] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-neutral-700 text-white rounded-xl font-medium transition-colors"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagement;
